import { useEffect, useMemo, useState } from 'react'
import { ChevronLeft, ChevronRight, Download, Edit, Eye, RefreshCw, Search } from 'lucide-react'
import apiClient from '../../api/axiosClient'
import { useAuth } from '../../contexts/AuthContext'
import ReceiptEditor from '../../components/patients/ReceiptEditor'

const pageSize = 6

const CMODashboard = () => {
  const { user } = useAuth()
  const [bills, setBills] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [editingBill, setEditingBill] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)

  const loadBills = async () => {
    try {
      setLoading(true)
      setError('')
      const response = await apiClient.get('/insurance/ip-final-bills', {
        params: {
          page: 1,
          per_page: 200,
          search: searchTerm || undefined
        }
      })
      setBills(response.data.receipts || [])
      setCurrentPage(1)
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load IP final bills')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadBills()
  }, [])

  const viewBill = async (receiptId) => {
    try {
      const response = await apiClient.get(`/insurance/receipts/${receiptId}/download`, {
        responseType: 'blob'
      })
      const blob = new Blob([response.data], { type: 'application/pdf' })
      const url = window.URL.createObjectURL(blob)
      window.open(url, '_blank')
      setTimeout(() => window.URL.revokeObjectURL(url), 1000)

      if (user?.role === 'super_admin') {
        await apiClient.post(`/insurance/ip-final-bills/${receiptId}/viewed`)
        setBills((prev) => prev.map((bill) => (
          bill.receipt_id === receiptId
            ? { ...bill, viewed_by_current_user: true }
            : bill
        )))
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to view IP final bill')
    }
  }

  const downloadBill = async (bill) => {
    try {
      const response = await apiClient.get(`/insurance/receipts/${bill.receipt_id}/download`, {
        responseType: 'blob'
      })
      const blob = new Blob([response.data], { type: 'application/pdf' })
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `IP_Final_Bill_${bill.receipt_no}_${bill.receipt_date}.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to download IP final bill')
    }
  }

  const saveBill = async (receiptId, updateData) => {
    try {
      await apiClient.put(`/insurance/receipts/${receiptId}`, updateData)
      setEditingBill(null)
      loadBills()
    } catch (err) {
      throw err
    }
  }

  const totalPages = Math.max(1, Math.ceil(bills.length / pageSize))
  const paginatedBills = useMemo(() => {
    const start = (currentPage - 1) * pageSize
    return bills.slice(start, start + pageSize)
  }, [bills, currentPage])
  const pageStart = bills.length === 0 ? 0 : (currentPage - 1) * pageSize + 1
  const pageEnd = Math.min(currentPage * pageSize, bills.length)

  const goToPage = (page) => {
    setCurrentPage(Math.min(Math.max(page, 1), totalPages))
  }

  return (
    <div className="bg-white rounded-xl p-6 shadow-md space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Insurance Manager Dashboard</h2>
          <p className="text-gray-600 mt-1">Review and download generated IP final bills.</p>
        </div>
        <button
          type="button"
          onClick={loadBills}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search size={18} className="absolute left-3 top-3 text-gray-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by patient, UHID, receipt no"
            className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <button
          type="button"
          onClick={loadBills}
          className="px-5 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
        >
          Search
        </button>
      </div>

      <div className="border border-gray-200 rounded-lg overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 border-b border-gray-200">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Receipt No</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Patient</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Date</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Amount</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-gray-600">Loading IP final bills...</td>
              </tr>
            ) : bills.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-gray-600">No IP final bills found</td>
              </tr>
            ) : (
              paginatedBills.map((bill) => (
                <tr key={bill.receipt_id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3 font-medium text-gray-900">{bill.receipt_no}</td>
                  <td className="px-4 py-3">
                    <div className="text-sm">
                      <div className="font-medium text-gray-900">{bill.patient?.full_name || 'N/A'}</div>
                      <div className="text-gray-500">{bill.patient?.uhid_no || 'N/A'}</div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm">
                      <div className="text-gray-900">{bill.receipt_date}</div>
                      <div className="text-gray-500">{bill.receipt_time}</div>
                    </div>
                  </td>
                  <td className="px-4 py-3 font-semibold text-gray-900">{bill.total_amount?.toFixed(2)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => viewBill(bill.receipt_id)}
                        disabled={user?.role === 'super_admin' && bill.viewed_by_current_user}
                        className="p-1 text-purple-600 hover:bg-purple-100 rounded transition-colors disabled:text-gray-400 disabled:hover:bg-transparent disabled:cursor-not-allowed"
                        title={
                          user?.role === 'super_admin' && bill.viewed_by_current_user
                            ? 'You have already viewed this bill. If you need to access it again, please contact the Insurance Manager'
                            : 'View PDF'
                        }
                      >
                        <Eye size={16} />
                      </button>
                      {user?.role === 'super_admin' && bill.viewed_by_current_user && (
                        <span className="text-xs text-red-600">
                          You have already viewed this bill. If you need to access it again, please contact the Insurance Manager
                        </span>
                      )}
                      {user?.role === 'cmo' && (
                        <>
                          <button
                            type="button"
                            onClick={() => downloadBill(bill)}
                            className="p-1 text-blue-600 hover:bg-blue-100 rounded transition-colors"
                            title="Download PDF"
                          >
                            <Download size={16} />
                          </button>
                          <button
                            type="button"
                            onClick={() => setEditingBill(bill)}
                            className="p-1 text-orange-600 hover:bg-orange-100 rounded transition-colors"
                            title="Edit Bill"
                          >
                            <Edit size={16} />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        {bills.length > pageSize && (
          <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3">
            <div className="text-sm text-gray-600">
              Showing {pageStart}-{pageEnd} of {bills.length}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1}
                className="inline-flex items-center gap-1 px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft size={16} />
                Prev
              </button>
              {Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => (
                <button
                  key={page}
                  type="button"
                  onClick={() => goToPage(page)}
                  className={`h-9 min-w-9 px-3 rounded-lg text-sm font-medium ${
                    currentPage === page
                      ? 'bg-blue-600 text-white'
                      : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {page}
                </button>
              ))}
              <button
                type="button"
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="inline-flex items-center gap-1 px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      {editingBill && (
        <ReceiptEditor
          receipt={editingBill}
          patient={editingBill.patient}
          onClose={() => setEditingBill(null)}
          onSave={saveBill}
        />
      )}
    </div>
  )
}

export default CMODashboard
